/**
 * FeedbackCell — single-column free-text feedback display.
 *
 * Compact amber line that expands on hover to a wide Radix tooltip with the
 * full message. Used inside TraceTable as its own column (desktop) or as a
 * line inside the mobile card. Kept tiny on purpose — the container owns
 * layout, we just own "show a tiny `FB` label + 2-line clamp + tooltip".
 *
 * Parent MUST wrap this in a <TooltipProvider>.
 */

import * as React from 'react';

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

export interface FeedbackCellProps {
  text: string | null;
  /** When true, render a bare placeholder em-dash instead of null. Desktop cells use this; mobile cards omit the row entirely. */
  placeholder?: boolean;
}

export function FeedbackCell({
  text,
  placeholder = false,
}: FeedbackCellProps): React.ReactElement | null {
  if (!text) {
    return placeholder ? (
      <span className="text-muted-foreground/50">—</span>
    ) : null;
  }
  return (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>
        <div className="flex min-w-0 items-baseline gap-1.5 cursor-default leading-snug">
          <span
            aria-hidden="true"
            className="shrink-0 font-semibold tabular-nums text-[10px] uppercase tracking-wide text-amber-600 dark:text-amber-400"
          >
            FB
          </span>
          <span className="line-clamp-2 min-w-0 flex-1 break-words italic text-foreground/90">
            {text}
          </span>
        </div>
      </TooltipTrigger>
      <TooltipContent
        side="left"
        align="start"
        sideOffset={8}
        collisionPadding={16}
        // `text-left` overrides the default centered-text on TooltipContent —
        // multi-line free-form feedback quotes read unprofessional when
        // centered. Numeric chart tooltips keep the centered default.
        className="w-[50vw] max-w-[50vw] max-h-[70vh] overflow-y-auto whitespace-pre-wrap break-words text-left text-xs leading-relaxed"
      >
        {text}
      </TooltipContent>
    </Tooltip>
  );
}
